import type { FocusEvent, FormEvent, ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Info, LoaderCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { PasswordField } from "@/components/PasswordField";
import { useSession } from "@/context/SessionContext";
import {
  ApiRequestError,
  adminSignIn,
  candidateSignIn,
  candidateSignUp,
  requestPasswordReset
} from "@/lib/api";
import { savePendingOtpChallenge } from "@/lib/pending-otp";
import { savePendingPasswordResetState } from "@/lib/pending-password-reset";
import { z } from "zod";

type Role = "admin" | "candidate";
type Mode = "forgot" | "signin" | "signup";

const candidateSignUpDefaults = {
  fullName: "",
  email: "",
  phone: "",
  serialNumber: "",
  university: "",
  password: "",
  confirmPassword: "",
  acceptDataProcessingConsent: false
};

const signInDefaults = {
  email: "",
  password: ""
};

const forgotDefaults = {
  email: ""
};

type CandidateSignUpForm = typeof candidateSignUpDefaults;
type ForgotPasswordForm = typeof forgotDefaults;
type SignInForm = typeof signInDefaults;
type AuthFormKey = "adminSignIn" | "candidateSignIn" | "candidateSignUp" | "forgotPassword";
type AuthValidationErrors = Record<AuthFormKey, Record<string, string>>;

const emailSchema = z
  .string()
  .trim()
  .min(1, "Email is required.")
  .email("Please enter a valid email address.");
const signInPasswordSchema = z
  .string()
  .min(1, "Password is required.")
  .min(8, "Password must be at least 8 characters.");
const registrationPasswordSchema = z
  .string()
  .min(1, "Password is required.")
  .min(8, "Password must be at least 8 characters.")
  .regex(/[a-z]/, "Password must include a lowercase letter.")
  .regex(/[A-Z]/, "Password must include an uppercase letter.")
  .regex(/\d/, "Password must include a number.")
  .regex(/[^A-Za-z0-9]/, "Password must include a special character.");
const signInSchema = z.object({
  email: emailSchema,
  password: signInPasswordSchema
});
const adminForgotPasswordSchema = z.object({
  email: emailSchema
});

function isValidCandidatePasswordResetIdentifier(value: string) {
  if (isEmailAddress(value)) {
    return true;
  }

  const compact = value.trim().replace(/\D/g, "");

  if (/^\d{10}$/.test(compact)) {
    return true;
  }

  if (/^91\d{10}$/.test(compact)) {
    return true;
  }

  return false;
}

const candidateForgotPasswordSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email or mobile number is required.")
    .refine(
      (value) => isValidCandidatePasswordResetIdentifier(value),
      "Please enter a valid email address or 10-digit mobile number."
    )
});

function isValidCandidatePhoneNumber(value: string) {
  const compact = value.trim().replace(/\D/g, "");

  if (!compact) {
    return true;
  }

  return /^\d{10}$/.test(compact);
}

function isEmailAddress(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function formatCandidatePhoneNumber(value: string) {
  const compact = value.trim().replace(/\D/g, "");

  if (!compact) {
    return undefined;
  }

  return `+91 ${compact}`;
}

function isAllowedPhoneInputKey(key: string) {
  return (
    /^\d$/.test(key) ||
    [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End"
    ].includes(key)
  );
}

const candidateSignUpSchema = z
  .object({
    acceptDataProcessingConsent: z
      .boolean()
      .refine((value) => value, "Please accept the DPDP notice and consent."),
    confirmPassword: z
      .string()
      .min(1, "Please confirm your password.")
      .min(8, "Confirm password must be at least 8 characters."),
    email: emailSchema,
    fullName: z.string().trim().min(2, "Please enter your full name."),
    serialNumber: z.string().trim().min(1, "Serial number is required."),
    university: z.string().trim().min(2, "College / university is required.").regex(/^[^0-9]*$/, "Numbers are not allowed in the college/university name."),
    password: registrationPasswordSchema,
    phone: z
      .string()
      .trim()
      .refine(
        (value) => isValidCandidatePhoneNumber(value),
        "Please enter a 10-digit mobile number."
      )
  })
  .superRefine((form, context) => {
    if (form.password !== form.confirmPassword) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password and confirmation do not match.",
        path: ["confirmPassword"]
      });
    }
  });

const passwordGuidelines = [
  {
    id: "length",
    label: "At least 8 characters",
    test: (password: string) => password.length >= 8
  },
  {
    id: "uppercase",
    label: "One uppercase letter",
    test: (password: string) => /[A-Z]/.test(password)
  },
  {
    id: "lowercase",
    label: "One lowercase letter",
    test: (password: string) => /[a-z]/.test(password)
  },
  {
    id: "number",
    label: "One number",
    test: (password: string) => /\d/.test(password)
  },
  {
    id: "symbol",
    label: "One symbol",
    test: (password: string) => /[^A-Za-z0-9]/.test(password)
  }
] as const;

function createEmptyAuthValidationErrors(): AuthValidationErrors {
  return {
    adminSignIn: {},
    candidateSignIn: {},
    candidateSignUp: {},
    forgotPassword: {}
  };
}

function collectZodFieldErrors(error: z.ZodError) {
  return error.issues.reduce<Record<string, string>>((fieldErrors, issue) => {
    const fieldName = String(issue.path[0] ?? "");

    if (fieldName && !fieldErrors[fieldName]) {
      fieldErrors[fieldName] = issue.message;
    }

    return fieldErrors;
  }, {});
}

function getZodFieldError(error: z.ZodError, fieldName: string) {
  return error.issues.find((issue) => issue.path[0] === fieldName)?.message;
}

function getFieldErrorId(formKey: AuthFormKey, fieldName: string) {
  return `${formKey}-${fieldName}-error`;
}

function getInputClassName(error?: string, baseClassName = "input-field") {
  return [
    baseClassName,
    error ? "border-rose-400 bg-rose-50/70 focus:border-rose-500 focus:ring-rose-500/15" : ""
  ]
    .filter(Boolean)
    .join(" ");
}

function shouldSkipFieldBlurValidation(event: FocusEvent<HTMLElement>) {
  const nextElement = event.relatedTarget;

  return nextElement instanceof HTMLElement
    ? Boolean(nextElement.closest("a, button, [role='button']"))
    : false;
}

function getPasswordStrength(password: string) {
  const metGuidelines = passwordGuidelines.filter((guideline) => guideline.test(password));
  const score = metGuidelines.length;

  if (!password) {
    return {
      barClassName: "bg-line",
      label: "No password yet",
      percentage: 0
    };
  }

  if (score <= 2) {
    return {
      barClassName: "bg-rose-500",
      label: "Weak",
      percentage: Math.max(20, score * 20)
    };
  }

  if (score === 3) {
    return {
      barClassName: "bg-amber-500",
      label: "Fair",
      percentage: 60
    };
  }

  if (score === 4) {
    return {
      barClassName: "bg-sky-500",
      label: "Strong",
      percentage: 80
    };
  }

  return {
    barClassName: "bg-emerald-500",
    label: "Very strong",
    percentage: 100
  };
}

export function AuthPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isReady, session, setSession } = useSession();

  const role = (searchParams.get("role") === "admin" ? "admin" : "candidate") satisfies Role;
  const requestedMode = searchParams.get("mode");
  const mode = (
    role === "admin"
      ? requestedMode === "forgot"
        ? "forgot"
        : "signin"
      : requestedMode === "signup"
        ? "signup"
        : requestedMode === "forgot"
          ? "forgot"
          : "signin"
  ) satisfies Mode;

  const [candidateSignUpForm, setCandidateSignUpForm] = useState(candidateSignUpDefaults);
  const [candidateSignInForm, setCandidateSignInForm] = useState(signInDefaults);
  const [adminForm, setAdminForm] = useState(signInDefaults);
  const [forgotPasswordForm, setForgotPasswordForm] = useState(forgotDefaults);
  const [validationErrors, setValidationErrors] = useState<AuthValidationErrors>(
    createEmptyAuthValidationErrors
  );
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formCopy = useMemo(() => {
    if (role === "admin" && mode === "forgot") {
      return {
        eyebrow: "Portal Access",
        title: "Reset your password",
        description:
          "Enter your administrator email address and we'll send a one-time password if the account exists."
      };
    }

    if (role === "admin") {
      return {
        eyebrow: "Portal Access",
        title: "Administrator sign in",
        description:
          "Use your administrator email and password to continue."
      };
    }

    if (mode === "signup") {
      return {
        eyebrow: "Portal Access",
        title: "Create your account",
        description:
          "Create your account to begin the onboarding process. A verification code will be sent to your registered email address in the next step."
      };
    }

    if (mode === "forgot") {
      return {
        eyebrow: "Portal Access",
        title: "Reset your password",
        description:
          "Enter your email address or mobile number and we'll send a one-time password if the account exists."
      };
    }

    return {
      eyebrow: "Portal Access",
      title: "Sign in to your account",
      description:
        "Use your email address and password to continue."
    };
  }, [mode, role]);
  const candidatePasswordStrength = useMemo(
    () => getPasswordStrength(candidateSignUpForm.password),
    [candidateSignUpForm.password]
  );
  const isAdminForgotReady = useMemo(
    () => adminForgotPasswordSchema.safeParse(forgotPasswordForm).success,
    [forgotPasswordForm]
  );
  const isAdminSignInReady = useMemo(
    () => signInSchema.safeParse(adminForm).success,
    [adminForm]
  );
  const isCandidateForgotReady = useMemo(
    () => candidateForgotPasswordSchema.safeParse(forgotPasswordForm).success,
    [forgotPasswordForm]
  );
  const isCandidateSignInReady = useMemo(
    () => signInSchema.safeParse(candidateSignInForm).success,
    [candidateSignInForm]
  );
  const isCandidateSignUpReady = useMemo(
    () => candidateSignUpSchema.safeParse(candidateSignUpForm).success,
    [candidateSignUpForm]
  );
  const hasConfirmPassword = candidateSignUpForm.confirmPassword.length > 0;
  const doPasswordsMatch =
    hasConfirmPassword && candidateSignUpForm.password === candidateSignUpForm.confirmPassword;
  const adminForgotEmailError = validationErrors.forgotPassword.email;
  const adminSignInEmailError = validationErrors.adminSignIn.email;
  const adminSignInPasswordError = validationErrors.adminSignIn.password;
  const candidateForgotEmailError = validationErrors.forgotPassword.email;
  const candidateSignInEmailError = validationErrors.candidateSignIn.email;
  const candidateSignInPasswordError = validationErrors.candidateSignIn.password;
  const candidateSignUpConsentError = validationErrors.candidateSignUp.acceptDataProcessingConsent;
  const candidateSignUpConfirmPasswordError = validationErrors.candidateSignUp.confirmPassword;
  const candidateSignUpEmailError = validationErrors.candidateSignUp.email;
  const candidateSignUpFullNameError = validationErrors.candidateSignUp.fullName;
  const candidateSignUpPasswordError = validationErrors.candidateSignUp.password;
  const candidateSignUpPhoneError = validationErrors.candidateSignUp.phone;

  function applyApiValidationErrors(
    formKey: AuthFormKey,
    requestError: unknown,
    fallbackMessage: string
  ) {
    if (requestError instanceof ApiRequestError) {
      const hasFieldErrors = Object.keys(requestError.fieldErrors).length > 0;

      if (hasFieldErrors) {
        setValidationErrors((current) => ({
          ...current,
          [formKey]: {
            ...current[formKey],
            ...requestError.fieldErrors
          }
        }));
        setError(requestError.formErrors[0] ?? null);
        return;
      }

      setError(requestError.message || fallbackMessage);
      return;
    }

    setError(requestError instanceof Error ? requestError.message : fallbackMessage);
  }

  function updateSearch(nextRole: Role, nextMode: Mode) {
    setSearchParams({ role: nextRole, mode: nextMode });
    setError(null);
    setMessage(null);
    setValidationErrors(createEmptyAuthValidationErrors());
  }

  function handleRoleChange(nextRole: Role) {
    updateSearch(nextRole, "signin");
  }

  function validateAuthForm<TSchema extends z.ZodTypeAny>(
    formKey: AuthFormKey,
    schema: TSchema,
    values: unknown
  ) {
    const result = schema.safeParse(values);

    setValidationErrors((current) => ({
      ...current,
      [formKey]: result.success ? {} : collectZodFieldErrors(result.error)
    }));

    return result;
  }

  function validateAuthField<TSchema extends z.ZodTypeAny>(
    formKey: AuthFormKey,
    schema: TSchema,
    values: unknown,
    fieldName: string
  ) {
    const result = schema.safeParse(values);
    const fieldError = result.success ? undefined : getZodFieldError(result.error, fieldName);

    setValidationErrors((current) => {
      const nextFormErrors = { ...current[formKey] };

      if (fieldError) {
        nextFormErrors[fieldName] = fieldError;
      } else {
        delete nextFormErrors[fieldName];
      }

      return {
        ...current,
        [formKey]: nextFormErrors
      };
    });

    return !fieldError;
  }

  function validateAuthFieldOnBlur<TSchema extends z.ZodTypeAny>(
    event: FocusEvent<HTMLElement>,
    formKey: AuthFormKey,
    schema: TSchema,
    values: unknown,
    fieldName: string
  ) {
    if (shouldSkipFieldBlurValidation(event)) {
      return;
    }

    window.setTimeout(() => validateAuthField(formKey, schema, values, fieldName), 0);
  }

  function updateCandidateSignUpField<FieldName extends keyof CandidateSignUpForm>(
    fieldName: FieldName,
    value: CandidateSignUpForm[FieldName]
  ) {
    const normalizedValue = (
      fieldName === "phone" && typeof value === "string"
        ? value.replace(/\D/g, "").slice(0, 10)
        : value
    ) as CandidateSignUpForm[FieldName];

    const nextForm = {
      ...candidateSignUpForm,
      [fieldName]: normalizedValue
    };

    setCandidateSignUpForm(nextForm);

    if (validationErrors.candidateSignUp[String(fieldName)]) {
      validateAuthField("candidateSignUp", candidateSignUpSchema, nextForm, String(fieldName));
    }

    if (fieldName === "password" && validationErrors.candidateSignUp.confirmPassword) {
      validateAuthField("candidateSignUp", candidateSignUpSchema, nextForm, "confirmPassword");
    }
  }

  function updateCandidateSignInField<FieldName extends keyof SignInForm>(
    fieldName: FieldName,
    value: SignInForm[FieldName]
  ) {
    const nextForm = {
      ...candidateSignInForm,
      [fieldName]: value
    };

    setCandidateSignInForm(nextForm);

    if (validationErrors.candidateSignIn[String(fieldName)]) {
      validateAuthField("candidateSignIn", signInSchema, nextForm, String(fieldName));
    }
  }

  function updateAdminSignInField<FieldName extends keyof SignInForm>(
    fieldName: FieldName,
    value: SignInForm[FieldName]
  ) {
    const nextForm = {
      ...adminForm,
      [fieldName]: value
    };

    setAdminForm(nextForm);

    if (validationErrors.adminSignIn[String(fieldName)]) {
      validateAuthField("adminSignIn", signInSchema, nextForm, String(fieldName));
    }
  }

  function updateForgotPasswordField<FieldName extends keyof ForgotPasswordForm>(
    fieldName: FieldName,
    value: ForgotPasswordForm[FieldName]
  ) {
    const nextForm = {
      ...forgotPasswordForm,
      [fieldName]: value
    };

    setForgotPasswordForm(nextForm);

    if (validationErrors.forgotPassword[String(fieldName)]) {
      validateAuthField(
        "forgotPassword",
        role === "admin" ? adminForgotPasswordSchema : candidateForgotPasswordSchema,
        nextForm,
        String(fieldName)
      );
    }
  }

  async function handleCandidateSignUp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    const validationResult = validateAuthForm(
      "candidateSignUp",
      candidateSignUpSchema,
      candidateSignUpForm
    );

    if (!validationResult.success) {
      return;
    }

    const payload = {
      acceptDataProcessingConsent: validationResult.data.acceptDataProcessingConsent,
      email: validationResult.data.email,
      fullName: validationResult.data.fullName,
      serialNumber: validationResult.data.serialNumber,
      university: validationResult.data.university,
      password: validationResult.data.password,
      phone: formatCandidatePhoneNumber(validationResult.data.phone)
    };

    setIsSubmitting(true);

    try {
      const response = await candidateSignUp(payload);
      savePendingOtpChallenge(response.challenge);
      navigate("/otp");
    } catch (requestError) {
      applyApiValidationErrors("candidateSignUp", requestError, "Unable to create account.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCandidateSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationResult = validateAuthForm("candidateSignIn", signInSchema, candidateSignInForm);

    setError(null);
    setMessage(null);

    if (!validationResult.success) {
      return;
    }

    const payload = validationResult.data;

    setCandidateSignInForm(payload);
    setIsSubmitting(true);

    try {
      const response = await candidateSignIn(payload);
      setSession({
        role: "candidate",
        token: response.token,
        expiresAt: response.expiresAt,
        user: response.user
      });
      navigate("/candidate");
    } catch (requestError) {
      applyApiValidationErrors("candidateSignIn", requestError, "Unable to sign in.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleAdminSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationResult = validateAuthForm("adminSignIn", signInSchema, adminForm);

    setError(null);
    setMessage(null);

    if (!validationResult.success) {
      return;
    }

    const payload = validationResult.data;

    setAdminForm(payload);
    setIsSubmitting(true);

    try {
      const response = await adminSignIn(payload);
      setSession({
        role: "admin",
        token: response.token,
        expiresAt: response.expiresAt,
        user: response.user
      });
      navigate("/admin");
    } catch (requestError) {
      applyApiValidationErrors("adminSignIn", requestError, "Unable to sign in.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleForgotPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const activeForgotPasswordSchema = role === "admin"
      ? adminForgotPasswordSchema
      : candidateForgotPasswordSchema;
    const validationResult = validateAuthForm(
      "forgotPassword",
      activeForgotPasswordSchema,
      forgotPasswordForm
    );

    setError(null);
    setMessage(null);

    if (!validationResult.success) {
      return;
    }

    const payload = validationResult.data;

    setForgotPasswordForm(payload);
    setIsSubmitting(true);

    try {
      const response = await requestPasswordReset({
        identifier: payload.email,
        role
      });
      if (response.challenge) {
        savePendingPasswordResetState({
          challenge: response.challenge
        });
        setForgotPasswordForm(forgotDefaults);
        navigate(`/reset-password?role=${role}`);
        return;
      }

      setMessage(response.message);
      setForgotPasswordForm(forgotDefaults);
    } catch (requestError) {
      applyApiValidationErrors(
        "forgotPassword",
        requestError,
        "Unable to request a password reset."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!isReady) {
    return (
      <section className="flex justify-center px-4 py-10 md:px-6 md:py-16" data-testid="auth-page">
        <div className="w-full max-w-[34rem] rounded-[28px] border border-line/80 bg-white/96 p-6 shadow-[0_22px_56px_rgba(15,23,42,0.08)] md:p-8">
          <p className="eyebrow">Portal Access</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">Checking your session</h2>
        </div>
      </section>
    );
  }

  if (session) {
    return <Navigate replace to={session.role === "admin" ? "/admin" : "/candidate"} />;
  }

  return (
    <section className="flex justify-center px-4 py-10 md:px-6 md:py-16" data-testid="auth-page">
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="w-full overflow-hidden rounded-[28px] border border-line/80 bg-white/96 p-6 shadow-[0_22px_56px_rgba(15,23,42,0.08)] md:p-8"
        data-auth-mode={mode}
        data-auth-role={role}
        data-testid="auth-card"
        initial={{ opacity: 0, y: 20 }}
        style={{ maxWidth: "34rem" }}
        transition={{ delay: 0.06, duration: 0.45, ease: "easeOut" }}
      >
        <div className="w-full">
          <div className="border-b border-line/70 pb-6">
            <div className="min-w-0">
              <p className="eyebrow" data-testid="auth-eyebrow">
                {formCopy.eyebrow}
              </p>
              <h2
                className="mt-2 max-w-[11ch] text-3xl font-semibold tracking-tight text-ink md:text-[2.65rem] md:leading-[1.08]"
                data-testid="auth-heading"
              >
                {formCopy.title}
              </h2>
              <p className="mt-3 max-w-[30rem] text-base leading-8 text-slate" data-testid="auth-description">
                {formCopy.description}
              </p>
            </div>

            <div className="mt-5 w-full max-w-[200px]">
              <label className="block">
                <span className="sr-only">Access type</span>
                <div
                  className="relative inline-flex w-full items-center rounded-[16px] border border-line/80 bg-cloud/78 px-4 py-1 shadow-sm"
                  data-testid="auth-role-select-shell"
                >
                  <span className="mr-3 whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.18em] text-slate/56">
                    Role
                  </span>
                  <select
                    className="w-full min-w-0 appearance-none bg-transparent py-2 pr-8 text-sm font-semibold text-ink outline-none"
                    data-testid="auth-role-select"
                    onChange={(event) => handleRoleChange(event.target.value as Role)}
                    value={role}
                  >
                    <option value="candidate">Candidate</option>
                    <option value="admin">Administrator</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate/80" />
                </div>
              </label>
            </div>
          </div>

          {role === "admin" ? (
            <AnimatePresence initial={false} mode="wait">
              {mode === "forgot" ? (
                <motion.form
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  className="mt-6 grid gap-5"
                  data-testid="admin-forgot-form"
                  exit={{ opacity: 0, x: -10, y: 8 }}
                  initial={{ opacity: 0, x: 10, y: 8 }}
                  key="admin-forgot"
                  noValidate
                  onSubmit={handleForgotPassword}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <AuthField label="Email" required>
                    <input
                      aria-describedby={
                        adminForgotEmailError
                          ? getFieldErrorId("forgotPassword", "email")
                          : undefined
                      }
                      aria-invalid={adminForgotEmailError ? true : undefined}
                      autoComplete="section-admin-recovery email"
                      autoFocus
                      className={getInputClassName(adminForgotEmailError)}
                      data-testid="admin-forgot-email"
                      name="forgotPasswordEmail"
                      onBlur={(event) =>
                        validateAuthFieldOnBlur(
                          event,
                          "forgotPassword",
                          adminForgotPasswordSchema,
                          forgotPasswordForm,
                          "email"
                        )
                      }
                      onChange={(event) => updateForgotPasswordField("email", event.target.value)}
                      placeholder="Enter email address"
                      required
                      type="email"
                      value={forgotPasswordForm.email}
                    />
                    <FieldError
                      id={getFieldErrorId("forgotPassword", "email")}
                      message={adminForgotEmailError}
                      testId="admin-forgot-email-error"
                    />
                  </AuthField>

                  <MessageBlock error={error} message={message} />

                  <div className="flex flex-col gap-4 pt-1">
                    <button
                      className="btn-primary w-full justify-center"
                      data-testid="admin-forgot-submit"
                      disabled={isSubmitting || !isAdminForgotReady}
                      type="submit"
                    >
                      {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                      Send OTP
                    </button>

                    <AuthSwitchText
                      action="Back to Sign in"
                      onClick={() => updateSearch("admin", "signin")}
                      testId="admin-forgot-signin-link"
                    />
                  </div>
                </motion.form>
              ) : (
                <motion.form
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  className="mt-6 grid gap-5"
                  data-testid="admin-signin-form"
                  exit={{ opacity: 0, x: -10, y: 8 }}
                  initial={{ opacity: 0, x: 10, y: 8 }}
                  key="admin-signin"
                  noValidate
                  onSubmit={handleAdminSignIn}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <AuthField label="Email" required>
                    <input
                      aria-describedby={
                        adminSignInEmailError
                          ? getFieldErrorId("adminSignIn", "email")
                          : undefined
                      }
                      aria-invalid={adminSignInEmailError ? true : undefined}
                      autoComplete="section-admin username"
                      autoFocus
                      className={getInputClassName(adminSignInEmailError)}
                      data-testid="admin-signin-email"
                      name="adminSignInEmail"
                      onBlur={(event) =>
                        validateAuthFieldOnBlur(
                          event,
                          "adminSignIn",
                          signInSchema,
                          adminForm,
                          "email"
                        )
                      }
                      onChange={(event) => updateAdminSignInField("email", event.target.value)}
                      placeholder="Enter email address"
                      required
                      type="email"
                      value={adminForm.email}
                    />
                    <FieldError
                      id={getFieldErrorId("adminSignIn", "email")}
                      message={adminSignInEmailError}
                      testId="admin-signin-email-error"
                    />
                  </AuthField>

                  <AuthField label="Password" required>
                    <PasswordField
                      aria-describedby={
                        adminSignInPasswordError
                          ? getFieldErrorId("adminSignIn", "password")
                          : undefined
                      }
                      aria-invalid={adminSignInPasswordError ? true : undefined}
                      autoComplete="section-admin current-password"
                      data-testid="admin-signin-password"
                      inputClassName={getInputClassName(adminSignInPasswordError, "")}
                      minLength={8}
                      name="adminSignInPassword"
                      onBlur={(event) =>
                        validateAuthFieldOnBlur(
                          event,
                          "adminSignIn",
                          signInSchema,
                          adminForm,
                          "password"
                        )
                      }
                      onChange={(event) => updateAdminSignInField("password", event.target.value)}
                      placeholder="Enter password"
                      required
                      toggleButtonTestId="admin-signin-password-toggle"
                      value={adminForm.password}
                    />
                    <FieldError
                      id={getFieldErrorId("adminSignIn", "password")}
                      message={adminSignInPasswordError}
                      testId="admin-signin-password-error"
                    />
                  </AuthField>

                  <MessageBlock error={error} message={message} />

                  <div className="flex flex-col gap-4 pt-1">
                    <button
                      className="btn-primary w-full justify-center"
                      data-testid="admin-signin-submit"
                      disabled={isSubmitting || !isAdminSignInReady}
                      type="submit"
                    >
                      {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                      Sign In
                    </button>

                    <AuthSwitchText
                      action="Forgot password?"
                      onClick={() => updateSearch("admin", "forgot")}
                      testId="admin-signin-forgot-link"
                    />
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          ) : (
            <AnimatePresence initial={false} mode="wait">
                {mode === "signup" ? (
                  <motion.form
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    className="mt-6 grid gap-5"
                    data-testid="candidate-signup-form"
                    exit={{ opacity: 0, x: -10, y: 8 }}
                    initial={{ opacity: 0, x: 10, y: 8 }}
                    key="candidate-signup"
                    noValidate
                    onSubmit={handleCandidateSignUp}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <AuthField
                      label="Full name"
                      note="This will appear on the agreement record."
                      required
                    >
                      <input
                        aria-describedby={
                          candidateSignUpFullNameError
                            ? getFieldErrorId("candidateSignUp", "fullName")
                            : undefined
                        }
                        aria-invalid={candidateSignUpFullNameError ? true : undefined}
                        autoComplete="name"
                        autoFocus
                        className={getInputClassName(candidateSignUpFullNameError)}
                        data-testid="candidate-signup-full-name"
                        name="candidateSignUpFullName"
                        onBlur={(event) =>
                          validateAuthFieldOnBlur(
                            event,
                            "candidateSignUp",
                            candidateSignUpSchema,
                            candidateSignUpForm,
                            "fullName"
                          )
                        }
                        onChange={(event) =>
                          updateCandidateSignUpField("fullName", event.target.value)
                        }
                        placeholder="Enter full name"
                        required
                        value={candidateSignUpForm.fullName}
                      />
                      <FieldError
                        id={getFieldErrorId("candidateSignUp", "fullName")}
                        message={candidateSignUpFullNameError}
                        testId="candidate-signup-full-name-error"
                      />
                    </AuthField>

                    <AuthField label="Email" required>
                      <input
                        aria-describedby={
                          candidateSignUpEmailError
                            ? getFieldErrorId("candidateSignUp", "email")
                            : undefined
                        }
                        aria-invalid={candidateSignUpEmailError ? true : undefined}
                        autoComplete="section-candidate-signup email"
                        className={getInputClassName(candidateSignUpEmailError)}
                        data-testid="candidate-signup-email"
                        name="candidateSignUpEmail"
                        onBlur={(event) =>
                          validateAuthFieldOnBlur(
                            event,
                            "candidateSignUp",
                            candidateSignUpSchema,
                            candidateSignUpForm,
                            "email"
                          )
                        }
                        onChange={(event) =>
                          updateCandidateSignUpField("email", event.target.value)
                        }
                        placeholder="Enter email address"
                        required
                        type="email"
                        value={candidateSignUpForm.email}
                      />
                      <FieldError
                        id={getFieldErrorId("candidateSignUp", "email")}
                        message={candidateSignUpEmailError}
                        testId="candidate-signup-email-error"
                      />
                    </AuthField>

                    <AuthField label="Phone" note="Optional · Numbers only, 10 digits">
                      <div
                        className={getInputClassName(
                          candidateSignUpPhoneError,
                          "input-field flex items-center gap-3 px-4"
                        )}
                      >
                        <span className="shrink-0 border-r border-line/80 pr-3 text-sm font-semibold text-ink">
                          +91
                        </span>
                        <input
                          aria-describedby={
                            candidateSignUpPhoneError
                              ? getFieldErrorId("candidateSignUp", "phone")
                              : undefined
                          }
                          aria-invalid={candidateSignUpPhoneError ? true : undefined}
                          autoComplete="tel-national"
                          className="min-w-0 flex-1 border-0 bg-transparent px-0 py-0 text-base text-ink outline-none placeholder:text-slate/55"
                          data-testid="candidate-signup-phone"
                          inputMode="numeric"
                          maxLength={10}
                          name="candidateSignUpPhone"
                          onBlur={(event) =>
                            validateAuthFieldOnBlur(
                              event,
                              "candidateSignUp",
                              candidateSignUpSchema,
                              candidateSignUpForm,
                              "phone"
                            )
                          }
                          onChange={(event) =>
                            updateCandidateSignUpField("phone", event.target.value)
                          }
                          onKeyDown={(event) => {
                            if (
                              event.ctrlKey ||
                              event.metaKey ||
                              event.altKey ||
                              isAllowedPhoneInputKey(event.key)
                            ) {
                              return;
                            }

                            event.preventDefault();
                          }}
                          placeholder="Enter mobile number"
                          pattern="[0-9]{10}"
                          type="text"
                          value={candidateSignUpForm.phone}
                        />
                      </div>
                      <FieldError
                        id={getFieldErrorId("candidateSignUp", "phone")}
                        message={candidateSignUpPhoneError}
                        testId="candidate-signup-phone-error"
                      />
                    </AuthField>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <AuthField label="Serial number" note="College / campus serial number" required>
                        <input
                          aria-describedby={
                            validationErrors.candidateSignUp.serialNumber
                              ? getFieldErrorId("candidateSignUp", "serialNumber")
                              : undefined
                          }
                          aria-invalid={validationErrors.candidateSignUp.serialNumber ? true : undefined}
                          className={getInputClassName(validationErrors.candidateSignUp.serialNumber)}
                          data-testid="candidate-signup-serial-number"
                          name="candidateSignUpSerialNumber"
                          onBlur={(event) =>
                            validateAuthFieldOnBlur(
                              event,
                              "candidateSignUp",
                              candidateSignUpSchema,
                              candidateSignUpForm,
                              "serialNumber"
                            )
                          }
                          onChange={(event) =>
                            updateCandidateSignUpField("serialNumber", event.target.value)
                          }
                          placeholder="Enter serial number"
                          required
                          type="text"
                          value={candidateSignUpForm.serialNumber}
                        />
                        <FieldError
                          id={getFieldErrorId("candidateSignUp", "serialNumber")}
                          message={validationErrors.candidateSignUp.serialNumber}
                          testId="candidate-signup-serial-number-error"
                        />
                      </AuthField>

                      <AuthField label="College / University" required>
                        <input
                          aria-describedby={
                            validationErrors.candidateSignUp.university
                              ? getFieldErrorId("candidateSignUp", "university")
                              : undefined
                          }
                          aria-invalid={validationErrors.candidateSignUp.university ? true : undefined}
                          className={getInputClassName(validationErrors.candidateSignUp.university)}
                          data-testid="candidate-signup-university"
                          name="candidateSignUpUniversity"
                          onBlur={(event) =>
                            validateAuthFieldOnBlur(
                              event,
                              "candidateSignUp",
                              candidateSignUpSchema,
                              candidateSignUpForm,
                              "university"
                            )
                          }
                          onChange={(event) =>
                            updateCandidateSignUpField("university", event.target.value)
                          }
                          placeholder="Enter college / university name"
                          required
                          type="text"
                          value={candidateSignUpForm.university}
                        />
                        <FieldError
                          id={getFieldErrorId("candidateSignUp", "university")}
                          message={validationErrors.candidateSignUp.university}
                          testId="candidate-signup-university-error"
                        />
                      </AuthField>
                    </div>

                    <AuthField label="Password" note="Minimum 8 characters." required>
                      <PasswordField
                        aria-describedby={
                          candidateSignUpPasswordError
                            ? getFieldErrorId("candidateSignUp", "password")
                            : undefined
                        }
                        aria-invalid={candidateSignUpPasswordError ? true : undefined}
                        autoComplete="section-candidate-signup new-password"
                        data-testid="candidate-signup-password"
                        inputClassName={getInputClassName(candidateSignUpPasswordError, "")}
                        minLength={8}
                        name="candidateSignUpPassword"
                        onBlur={(event) =>
                          validateAuthFieldOnBlur(
                            event,
                            "candidateSignUp",
                            candidateSignUpSchema,
                            candidateSignUpForm,
                            "password"
                          )
                        }
                        onChange={(event) =>
                          updateCandidateSignUpField("password", event.target.value)
                        }
                        placeholder="Create password"
                        required
                        toggleButtonTestId="candidate-signup-password-toggle"
                        value={candidateSignUpForm.password}
                      />
                      <FieldError
                        id={getFieldErrorId("candidateSignUp", "password")}
                        message={candidateSignUpPasswordError}
                        testId="candidate-signup-password-error"
                      />
                      <PasswordStrengthGuide
                        password={candidateSignUpForm.password}
                        strength={candidatePasswordStrength}
                      />
                    </AuthField>

                    <AuthField label="Confirm password" required>
                      <PasswordField
                        aria-describedby={
                          candidateSignUpConfirmPasswordError
                            ? getFieldErrorId("candidateSignUp", "confirmPassword")
                            : undefined
                        }
                        aria-invalid={candidateSignUpConfirmPasswordError ? true : undefined}
                        autoComplete="section-candidate-signup new-password"
                        data-testid="candidate-signup-confirm-password"
                        inputClassName={getInputClassName(
                          candidateSignUpConfirmPasswordError,
                          ""
                        )}
                        minLength={8}
                        name="candidateSignUpConfirmPassword"
                        onBlur={(event) =>
                          validateAuthFieldOnBlur(
                            event,
                            "candidateSignUp",
                            candidateSignUpSchema,
                            candidateSignUpForm,
                            "confirmPassword"
                          )
                        }
                        onChange={(event) =>
                          updateCandidateSignUpField("confirmPassword", event.target.value)
                        }
                        placeholder="Confirm password"
                        required
                        toggleButtonTestId="candidate-signup-confirm-password-toggle"
                        value={candidateSignUpForm.confirmPassword}
                      />
                      <FieldError
                        id={getFieldErrorId("candidateSignUp", "confirmPassword")}
                        message={candidateSignUpConfirmPasswordError}
                        testId="candidate-signup-confirm-password-error"
                      />
                      {hasConfirmPassword ? (
                        <p
                          className={`text-xs font-medium ${
                            doPasswordsMatch ? "text-emerald-700" : "text-rose-700"
                          }`}
                          data-testid="candidate-signup-confirm-password-status"
                        >
                          {doPasswordsMatch
                            ? "Passwords match."
                            : "Password and confirmation do not match."}
                        </p>
                      ) : null}
                    </AuthField>

                    <label
                      className="rounded-[22px] border border-line/70 bg-cloud/70 p-5"
                      data-testid="candidate-signup-consent-shell"
                    >
                      <div className="flex items-start gap-3">
                        <input
                          aria-describedby={
                            candidateSignUpConsentError
                              ? getFieldErrorId(
                                  "candidateSignUp",
                                  "acceptDataProcessingConsent"
                                )
                              : undefined
                          }
                          aria-invalid={candidateSignUpConsentError ? true : undefined}
                          checked={candidateSignUpForm.acceptDataProcessingConsent}
                          className="mt-1 h-4 w-4 rounded border-line"
                          data-testid="candidate-signup-consent"
                          onBlur={(event) =>
                            validateAuthFieldOnBlur(
                              event,
                              "candidateSignUp",
                              candidateSignUpSchema,
                              candidateSignUpForm,
                              "acceptDataProcessingConsent"
                            )
                          }
                          onChange={(event) =>
                            updateCandidateSignUpField(
                              "acceptDataProcessingConsent",
                              event.target.checked
                            )
                          }
                          required
                          type="checkbox"
                        />
                        <div>
                          <p className="field-label">
                            DPDP notice and consent
                            <span className="ml-1 text-rose-600">*</span>
                          </p>
                          <p className="mt-2 text-sm leading-7 text-slate">
                            I agree that Gupio may process my sign-up data, agreement details,
                            signature, timestamps, and onboarding records for review, approval, and
                            compliance administration.
                          </p>
                          <FieldError
                            id={getFieldErrorId(
                              "candidateSignUp",
                              "acceptDataProcessingConsent"
                            )}
                            message={candidateSignUpConsentError}
                            testId="candidate-signup-consent-error"
                          />
                        </div>
                      </div>
                    </label>

                    <MessageBlock error={error} message={message} />

                    <div className="flex flex-col gap-4 pt-1">
                      <button
                        className="btn-primary w-full justify-center"
                        data-testid="candidate-signup-submit"
                        disabled={isSubmitting || !isCandidateSignUpReady}
                        type="submit"
                      >
                        {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                        Continue to Email Verification
                      </button>

                      <AuthSwitchText
                        action="Sign in"
                        onClick={() => updateSearch("candidate", "signin")}
                        prefix="Already have an account?"
                        testId="candidate-signup-signin-link"
                      />
                    </div>
                  </motion.form>
                ) : mode === "forgot" ? (
                  <motion.form
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    className="mt-6 grid gap-5"
                    data-testid="candidate-forgot-form"
                    exit={{ opacity: 0, x: -10, y: 8 }}
                    initial={{ opacity: 0, x: 10, y: 8 }}
                    key="candidate-forgot"
                    noValidate
                    onSubmit={handleForgotPassword}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <AuthField label="Email or mobile number" required>
                      <input
                        aria-describedby={
                          candidateForgotEmailError
                            ? getFieldErrorId("forgotPassword", "email")
                            : undefined
                        }
                        aria-invalid={candidateForgotEmailError ? true : undefined}
                        autoComplete="section-candidate-recovery email"
                        autoFocus
                        className={getInputClassName(candidateForgotEmailError)}
                        data-testid="candidate-forgot-email"
                        name="forgotPasswordEmail"
                        onBlur={(event) =>
                          validateAuthFieldOnBlur(
                            event,
                            "forgotPassword",
                            candidateForgotPasswordSchema,
                            forgotPasswordForm,
                            "email"
                          )
                        }
                        onChange={(event) => updateForgotPasswordField("email", event.target.value)}
                        placeholder="Enter email or 10-digit mobile number"
                        required
                        type="text"
                        value={forgotPasswordForm.email}
                      />
                      <FieldError
                        id={getFieldErrorId("forgotPassword", "email")}
                        message={candidateForgotEmailError}
                        testId="candidate-forgot-email-error"
                      />
                    </AuthField>

                    <MessageBlock error={error} message={message} />

                    <div className="flex flex-col gap-4 pt-1">
                      <button
                        className="btn-primary w-full justify-center"
                        data-testid="candidate-forgot-submit"
                        disabled={isSubmitting || !isCandidateForgotReady}
                        type="submit"
                    >
                      {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                      Send OTP
                    </button>

                      <AuthSwitchText
                        action="Back to Sign in"
                        onClick={() => updateSearch("candidate", "signin")}
                        testId="candidate-forgot-signin-link"
                      />
                    </div>
                  </motion.form>
                ) : (
                  <motion.form
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    className="mt-6 grid gap-5"
                    data-testid="candidate-signin-form"
                    exit={{ opacity: 0, x: -10, y: 8 }}
                    initial={{ opacity: 0, x: 10, y: 8 }}
                    key="candidate-signin"
                    noValidate
                    onSubmit={handleCandidateSignIn}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <AuthField label="Email" required>
                      <input
                        aria-describedby={
                          candidateSignInEmailError
                            ? getFieldErrorId("candidateSignIn", "email")
                            : undefined
                        }
                        aria-invalid={candidateSignInEmailError ? true : undefined}
                        autoComplete="section-candidate username"
                        autoFocus
                        className={getInputClassName(candidateSignInEmailError)}
                        data-testid="candidate-signin-email"
                        name="candidateSignInEmail"
                        onBlur={(event) =>
                          validateAuthFieldOnBlur(
                            event,
                            "candidateSignIn",
                            signInSchema,
                            candidateSignInForm,
                            "email"
                          )
                        }
                        onChange={(event) =>
                          updateCandidateSignInField("email", event.target.value)
                        }
                        placeholder="Enter email address"
                        required
                        type="email"
                        value={candidateSignInForm.email}
                      />
                      <FieldError
                        id={getFieldErrorId("candidateSignIn", "email")}
                        message={candidateSignInEmailError}
                        testId="candidate-signin-email-error"
                      />
                    </AuthField>

                    <AuthField label="Password" required>
                      <PasswordField
                        aria-describedby={
                          candidateSignInPasswordError
                            ? getFieldErrorId("candidateSignIn", "password")
                            : undefined
                        }
                        aria-invalid={candidateSignInPasswordError ? true : undefined}
                        autoComplete="section-candidate current-password"
                        data-testid="candidate-signin-password"
                        inputClassName={getInputClassName(candidateSignInPasswordError, "")}
                        minLength={8}
                        name="candidateSignInPassword"
                        onBlur={(event) =>
                          validateAuthFieldOnBlur(
                            event,
                            "candidateSignIn",
                            signInSchema,
                            candidateSignInForm,
                            "password"
                          )
                        }
                        onChange={(event) =>
                          updateCandidateSignInField("password", event.target.value)
                        }
                        placeholder="Enter password"
                        required
                        toggleButtonTestId="candidate-signin-password-toggle"
                        value={candidateSignInForm.password}
                      />
                      <FieldError
                        id={getFieldErrorId("candidateSignIn", "password")}
                        message={candidateSignInPasswordError}
                        testId="candidate-signin-password-error"
                      />
                    </AuthField>

                    <MessageBlock error={error} message={message} />

                    <div className="flex flex-col gap-4 pt-1">
                      <button
                        className="btn-primary w-full justify-center"
                        data-testid="candidate-signin-submit"
                        disabled={isSubmitting || !isCandidateSignInReady}
                        type="submit"
                      >
                        {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                        Sign In
                      </button>

                      <div className="space-y-2">
                        <AuthSwitchText
                          action="Sign up"
                          onClick={() => updateSearch("candidate", "signup")}
                          prefix="Don't have an account?"
                          testId="candidate-signin-signup-link"
                        />
                        <AuthSwitchText
                          action="Forgot password?"
                          onClick={() => updateSearch("candidate", "forgot")}
                          testId="candidate-signin-forgot-link"
                        />
                      </div>
                    </div>
                  </motion.form>
                )}
            </AnimatePresence>
          )}
        </div>
      </motion.div>
    </section>
  );
}

function AuthField({
  children,
  label,
  note,
  required = false
}: {
  children: ReactNode;
  label: string;
  note?: string;
  required?: boolean;
}) {
  return (
    <label className="block space-y-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="field-label">
          {label}
          {required ? <span className="ml-1 text-rose-600">*</span> : null}
        </span>
        {note ? <span className="field-note">{note}</span> : null}
      </div>
      {children}
    </label>
  );
}

function FieldError({
  id,
  message,
  testId
}: {
  id: string;
  message?: string;
  testId: string;
}) {
  if (!message) {
    return null;
  }

  return (
    <p className="text-sm font-medium text-rose-700" data-testid={testId} id={id} role="alert">
      {message}
    </p>
  );
}

function PasswordStrengthGuide({
  password,
  strength
}: {
  password: string;
  strength: ReturnType<typeof getPasswordStrength>;
}) {
  const shouldShowStrengthLabel = password.length > 0;

  return (
    <div className="space-y-3" data-testid="candidate-signup-password-strength">
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate/70">
            Password strength
          </span>
          <div className="relative flex items-center gap-2">
            {shouldShowStrengthLabel ? (
              <span
                className="text-xs font-semibold text-ink"
                data-testid="candidate-signup-password-strength-label"
              >
                {strength.label}
              </span>
            ) : null}
            <span
              aria-label="Password instructions"
              className="group inline-flex"
              data-testid="candidate-signup-password-info"
              role="button"
              tabIndex={0}
            >
              <Info className="h-4 w-4 text-slate transition group-hover:text-royal group-focus:text-royal" />
              <span
                className="pointer-events-none absolute right-0 top-7 z-20 hidden w-[min(22rem,calc(100vw-4rem))] rounded-lg border border-line bg-white p-4 text-left shadow-[0_18px_42px_rgba(15,23,42,0.14)] group-hover:block group-focus:block"
                data-testid="candidate-signup-password-tooltip"
              >
                <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate/70">
                  Password guidelines
                </span>
                <span className="mt-2 block text-xs leading-6 text-slate">
                  Create a stronger password with a mix of letters, numbers, and a symbol.
                </span>
                <span className="mt-3 grid gap-2 text-xs text-slate sm:grid-cols-2">
                  {passwordGuidelines.map((guideline) => {
                    const isMet = guideline.test(password);

                    return (
                      <span
                        className="flex items-center gap-2"
                        data-testid={`candidate-signup-password-rule-${guideline.id}`}
                        key={guideline.id}
                      >
                        <span
                          aria-hidden="true"
                          className={`flex h-4 w-4 items-center justify-center rounded-full border text-[10px] ${
                            isMet
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-line bg-white text-slate/60"
                          }`}
                        >
                          {isMet ? <Check className="h-3 w-3" /> : null}
                        </span>
                        <span className={isMet ? "font-medium text-emerald-800" : undefined}>
                          {guideline.label}
                        </span>
                      </span>
                    );
                  })}
                </span>
              </span>
            </span>
          </div>
        </div>
        <div
          aria-label={`Password strength: ${strength.label}`}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={strength.percentage}
          className="h-2 overflow-hidden rounded-lg bg-cloud"
          data-testid="candidate-signup-password-strength-progress"
          role="progressbar"
        >
          <div
            className={`h-full rounded-lg transition-all duration-300 ${strength.barClassName}`}
            data-testid="candidate-signup-password-strength-bar"
            style={{ width: `${strength.percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function AuthSwitchText({
  action,
  onClick,
  prefix,
  testId
}: {
  action: string;
  onClick: () => void;
  prefix?: string;
  testId?: string;
}) {
  return (
    <p className="text-center text-sm text-slate">
      {prefix ? <>{prefix} </> : null}
      <button
        className="font-semibold text-royal transition hover:text-navy"
        data-testid={testId}
        onClick={onClick}
        type="button"
      >
        {action}
      </button>
    </p>
  );
}

function MessageBlock({ error, message }: { error: string | null; message: string | null }) {
  if (!error && !message) {
    return null;
  }

  return (
    <>
      {error ? (
        <div
          className="rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700"
          data-testid="auth-error-message"
          role="alert"
        >
          {error}
        </div>
      ) : null}

      {message ? (
        <div
          className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-800"
          data-testid="auth-success-message"
          role="status"
        >
          {message}
        </div>
      ) : null}
    </>
  );
}
